import Link from 'next/link'
import styled from 'styled-components'
import { CDNImage } from '~/components/business/CDNImage'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useAuth } from '~/hooks/useAuth'
import { MessageEntry } from '~/components/pages/message/MessageEntry'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyBtn } from '~/components/ui/MyBtn'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react-lite'
import { Dropdown, Popover } from 'antd'
import { UserEntry } from '~/components/pages/user/UserEntry'
import { MenuOutlined } from '@ant-design/icons'
import { MyLink } from '~/components/ui/MyLink'
import { QRCodeCanvas } from 'qrcode.react'
import { MyIcon } from '~/components/ui/MyIcon'

import styles from './header.module.css'

export type HeaderType = 'no-margin' | 'normal'

const StyledHeader = styled.header<{
  type: HeaderType
  $dark: boolean
}>`
  background: ${props =>
    props.$dark ? props.theme['neutral-100'] : props.theme['neutral-900']};
  height: 56px;
  padding: 12px 0;
  z-index: 999;
  top: 0;
  position: sticky;
  ${props =>
    props.type === 'normal' &&
    `border-bottom: 1px solid ${
      props.$dark ? props.theme['neutral-300'] : props.theme['neutral-600']
    }`};
  ${props => props.type === 'normal' && 'margin-bottom: 24px'};
`

const navData = [
  {
    name: '首页',
    href: '/',
    exist: true,
  },
  {
    name: 'AI创作',
    href: '/ai',
    exist: true,
  },
  {
    name: 'AI大赛',
    href: '/ai-competition',
    exist: true,
  },
  {
    name: '咒语生成器',
    href: '/tag-generator',
    exist: true,
  },
  {
    name: 'AI实验室',
    href: '/lab',
    exist: true,
  },
  {
    name: '企业服务',
    href: '/enterprise-service',
    exist: false,
    host: process.env.NEXT_PUBLIC_WUJIEAI_PRO_HOST,
  },
  {
    name: '商用图片',
    href: '/',
    exist: false,
    isNew: false,
  },
]

const links = [
  {
    label: (
      <a
        className="flex items-center text-xs py-1"
        href={process.env.NEXT_PUBLIC_WUJIEBANTU_HOST}
        target="_blank"
        rel="nofollow noopener noreferrer"
      >
        <CDNImage className="w-5 mr-2" src="/images/wujie@2x.png" /> 无界版图
      </a>
    ),
    key: 'wujiebantu',
  },
]

export const Header: React.FC<{
  type: HeaderType
  className?: string
  dark?: boolean
  /** 显示header右侧模块 */
  headerShowRight?: boolean
}> = observer(({ type, className, dark = true, headerShowRight = true }) => {
  const router = useRouter()
  const { isLogin } = useAuth()
  const { store } = useStores()

  const onLogin = () => {
    store.modal?.setLoginVisible(true)
  }

  return (
    <StyledHeader type={type} className={className} $dark={dark}>
      <MyContainer className="flex justify-between items-center">
        <div className="flex items-center">
          <Link href="/">
            <CDNImage
              alt="无界AI"
              src={dark ? '/images/logo-1.svg' : '/images/logo--black.svg'}
              className="h-8 w-[83px]"
            />
          </Link>
          <div
            className={classNames(
              dark ? 'border-neutral-300' : 'border-neutral-600',
              'border-l-[2px] h-8 ml-6 mr-3'
            )}
          ></div>
          <ul className={classNames('mb-0 flex ', dark && 'font-semibold')}>
            {navData.map(item => (
              <li
                key={item.name}
                className="text-[14px] leading-4 px-3 relative"
              >
                <MyLink
                  href={item.href}
                  exist={item.exist}
                  host={item.host}
                  className={classNames(
                    dark ? 'hover:text-neutral-800' : 'hover:text-neutral-200',
                    [
                      router.pathname === item.href && item.exist
                        ? 'text-neutral-800'
                        : 'text-neutral-400',
                    ]
                  )}
                >
                  {item.name}
                  {item.isNew && (
                    <div className="absolute right-2 -top-4">
                      <img className="h-[14px]" src="/images/new.png" alt="" />
                    </div>
                  )}
                  {/* {item.isHot && (
                      <div className="absolute right-2 -top-4">
                        <img
                          className="h-[14px]"
                          src="/images/hot.png"
                          alt=""
                        />
                      </div>
                    )} */}
                </MyLink>
              </li>
            ))}
          </ul>
        </div>
        {headerShowRight && (
          <div className="flex items-center">
            <Popover
              overlayClassName={styles.popover}
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              content={
                <div className="bg-neutral-200 rounded-2xl text-center p-4">
                  <div className="w-[128px] h-[128px] bg-white p-1">
                    <QRCodeCanvas
                      value={'https://m.wujieai.com/download'}
                      size={120}
                    ></QRCodeCanvas>
                  </div>

                  <div className="text-neutral-500 mt-4 leading-none">
                    Android & iPhone
                  </div>
                </div>
              }
              placement="bottom"
              trigger={'hover'}
              zIndex={999}
            >
              <div className="relative w-6 h-6 flex items-center justify-center rounded-full border-neutral-300 border border-solid bg-primary cursor-pointer">
                <MyIcon
                  icon="mobile"
                  className="text-[12px] text-white block "
                />
              </div>
            </Popover>
            {isLogin ? (
              <>
                <MessageEntry />
                <UserEntry />
              </>
            ) : (
              <>
                <MyBtn
                  className="!h-8 !leading-4 !py-[6px] !ml-6"
                  $dark
                  onClick={onLogin}
                >
                  登录 / 注册
                </MyBtn>
              </>
            )}
            <Dropdown
              menu={{ items: links }}
              placement="bottomRight"
              trigger={['click']}
              overlayClassName="w-[100px] shodow-dm"
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
            >
              <MenuOutlined className="!text-neutral-400 hover:!text-primary cursor-pointer ml-6 transition-all" />
            </Dropdown>
          </div>
        )}
      </MyContainer>
    </StyledHeader>
  )
})
